import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import SystemUpdateAltIcon from "@material-ui/icons/SystemUpdateAlt";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import Portal from "@material-ui/core/Portal";
import { checkServerVersion, applyUpdateAndReload } from "../../helpers/versionChecker";

const useStyles = makeStyles((theme) => ({
  banner: {
    position: "fixed",
    top: 70,
    left: 0,
    right: 0,
    margin: "0 auto",
    zIndex: 99999,
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#0f172a",
    color: "#ffffff",
    padding: theme.spacing(1.5, 2.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    maxWidth: "550px",
    minWidth: "300px",
    width: "90%",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    [theme.breakpoints.down("xs")]: {
      width: "94%",
      minWidth: "260px",
      padding: theme.spacing(1, 1.2),
      top: 55,
      gap: theme.spacing(1),
    },
  },
  textContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
  pulseIcon: {
    animation: "$pulse 1.8s infinite",
    color: "#22c55e",
    fontSize: "1.7rem",
  },
  title: {
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: "0.8rem",
    opacity: 0.8,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  updateButton: {
    backgroundColor: "#22c55e",
    color: "#0f172a",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "8px",
    padding: "6px 14px",
    "&:hover": {
      backgroundColor: "#16a34a",
      color: "#ffffff",
    },
  },
  closeButton: {
    color: "rgba(255, 255, 255, 0.7)",
    padding: 6,
    "&:hover": {
      color: "#ffffff",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  appBarButton: {
    color: "#22c55e",
    animation: "$pulse 1.8s infinite",
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.15)", opacity: 0.8 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
}));

const VersionControl = ({ onUpdateStart, onUpdateComplete }) => {
  const classes = useStyles();
  const [hasUpdate, setHasUpdate] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const result = await checkServerVersion();
      if (result.hasUpdate) {
        setHasUpdate(true);
        setServerVersion(result.serverVersion);
        setBannerVisible(true);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    // 1. Checagem inicial na montagem
    checkVersion();

    // 2. Checagem quando o usuário volta à aba/app (ao reabrir o PWA)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    const handleFocus = () => {
      checkVersion();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // 3. Listener do Service Worker nativo (quando uma nova versão é ativada)
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        setHasUpdate(true);
        setBannerVisible(true);
      });
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkVersion]);

  const handleApplyUpdate = async () => {
    setIsUpdating(true);
    if (typeof onUpdateStart === "function") {
      onUpdateStart();
    }
    await applyUpdateAndReload(undefined, serverVersion);
    if (typeof onUpdateComplete === "function") {
      onUpdateComplete();
    }
  };

  if (!hasUpdate) {
    return null;
  }

  return (
    <>
      {/* Botão pulsante fixo no AppBar caso o usuário feche temporariamente o banner */}
      {!bannerVisible && (
        <Tooltip title="Nova atualização disponível! Clique para aplicar.">
          <IconButton
            className={classes.appBarButton}
            onClick={() => setBannerVisible(true)}
            size="small"
          >
            <SystemUpdateAltIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Banner de atualização renderizado via Portal para ficar no topo absoluto da tela acima de qualquer Drawer ou Modal */}
      <Portal>
        <Slide direction="down" in={bannerVisible} mountOnEnter unmountOnExit>
          <div className={classes.banner}>
            <div className={classes.textContainer}>
              <SystemUpdateAltIcon className={classes.pulseIcon} />
              <div>
                <div className={classes.title}>Nova versão disponível!</div>
                <div className={classes.subtitle}>
                  {serverVersion ? `Versão ${serverVersion} pronta para uso.` : "O sistema foi atualizado."}
                </div>
              </div>
            </div>
            <div className={classes.actions}>
              <Button
                className={classes.updateButton}
                variant="contained"
                onClick={handleApplyUpdate}
                disabled={isUpdating}
              >
                {isUpdating ? "Atualizando..." : "Atualizar Agora"}
              </Button>
              <IconButton
                className={classes.closeButton}
                size="small"
                onClick={() => setBannerVisible(false)}
                title="Lembrar mais tarde"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </Slide>
      </Portal>
    </>
  );
};

export default VersionControl;